"use client";

import { useEffect, useMemo, useState } from "react";
import "./OrgSwitcherModal.css";

/* -------------------------------------------------------------------------- */
/* Types                                                                        */
/* -------------------------------------------------------------------------- */

/** A single organization entry shown in the switcher list. */
export type OrgItem = {
  id: string;
  name: string;
  /** Optional short / abbreviated name used in search matching. */
  shortName?: string;
  type?: string;
  region?: string;
};

export interface OrgSwitcherModalProps {
  /** Full list of organizations to display. */
  orgs: OrgItem[];
  /** ID of the currently active organization (controls the "Current" badge). */
  activeOrgId?: string | null;
  /**
   * Optional subtitle shown below the modal heading.
   * @default "Select an organization to switch context."
   */
  subtitle?: string;
  /** Called when the user selects an organization. */
  onSelect: (orgId: string) => void;
  /** Called when the modal should close without a selection. */
  onClose: () => void;
}

/* -------------------------------------------------------------------------- */
/* Component                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Full-screen modal for browsing and switching between organizations.
 * All data and callbacks are supplied via props — no internal context or state.
 */
export function OrgSwitcherModal({
  orgs,
  activeOrgId,
  subtitle = "Select an organization to switch context.",
  onSelect,
  onClose,
}: OrgSwitcherModalProps) {
  const [query, setQuery] = useState("");

  /* Lock body scroll + Escape key while open */
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return orgs;
    return orgs.filter((o) => {
      const hay =
        `${o.name} ${o.shortName ?? ""} ${o.type} ${o.region}`.toLowerCase();
      return hay.includes(q);
    });
  }, [orgs, query]);

  function pick(o: OrgItem) {
    onSelect(o.id);
    onClose();
  }

  return (
    /* Overlay — fade-in, closes on backdrop click */
    <div
      className="osm-overlay fixed inset-0 z-[100] flex items-center justify-center px-6 py-10"
      style={{
        background: "color-mix(in oklab, var(--brand-navy) 30%, transparent)",
        backdropFilter: "blur(2px)",
        animation: "osm-fade 140ms ease-out",
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Switch organization"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClose();
      }}
    >
      {/* Modal panel — pop-in, stops backdrop click propagation */}
      {/* biome-ignore lint: intentional — modal panel stops backdrop-click/key propagation */}
      <div
        className="w-full max-w-[520px] max-h-[calc(100vh-80px)] flex flex-col bg-[var(--bg-white)] border border-[var(--border-default)] rounded-[var(--radius-container)] overflow-hidden"
        style={{
          boxShadow: "var(--shadow-lg)",
          animation: "osm-pop 160ms cubic-bezier(0.4,0,0.2,1)",
        }}
        role="presentation"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {/* ── Header ────────────────────────────────────────────────────── */}
        <header className="flex items-start justify-between gap-[18px] px-[22px] pt-5 pb-3 border-b border-[var(--border-subtle)]">
          <div>
            <h2 className="font-[family-name:var(--font-display)] text-[15px] font-semibold tracking-[-0.015em] mb-1">
              Switch organization
            </h2>
            <p className="text-[12.5px] text-[var(--fg-secondary)] leading-[1.45]">
              {subtitle}
            </p>
          </div>
          <button
            type="button"
            /* Reset default button appearance, then apply design system styles */
            className="appearance-none m-0 p-0 border-0 bg-transparent inline-flex items-center justify-center w-7 h-7 rounded-[var(--radius-sm)] text-[var(--fg-muted)] cursor-pointer transition-[background-color,color] duration-[120ms] hover:bg-[var(--bg-hover)] hover:text-[var(--fg-primary)]"
            onClick={onClose}
            aria-label="Close"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6l-12 12" />
            </svg>
          </button>
        </header>

        {/* ── Toolbar: search + count ────────────────────────────────────── */}
        <div className="flex gap-[10px] items-center px-[22px] py-3">
          {/* Search box — border lights up on focus-within */}
          <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-[var(--bg-canvas)] border border-transparent rounded-[var(--radius-sm)] text-[var(--fg-muted)] transition-[border-color,background-color] duration-[120ms] focus-within:bg-[var(--bg-white)] focus-within:border-[var(--fg-brand)] focus-within:text-[var(--fg-primary)]">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              className="flex-1 bg-transparent border-none outline-none text-[13px] text-[var(--fg-primary)] placeholder:text-[var(--fg-muted)]"
              placeholder="Search by name, type, or region"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              // biome-ignore lint/a11y/noAutofocus: org-switcher modal — expected
              autoFocus
            />
          </div>
          <span className="font-mono text-[11px] text-[var(--fg-muted)] whitespace-nowrap">
            {filtered.length} of {orgs.length}
          </span>
        </div>

        {/* ── Organization list ─────────────────────────────────────────── */}
        <div
          className="flex-1 overflow-y-auto px-3 pb-[14px] pt-1 flex flex-col gap-px"
          role="listbox"
          aria-label="Organizations"
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-[22px] text-[13px] text-[var(--fg-muted)] text-center">
              No organizations match &ldquo;{query}&rdquo;.
            </div>
          ) : (
            filtered.map((o) => {
              const isActive = o.id === activeOrgId;
              return (
                <button
                  key={o.id}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={[
                    // Reset default button appearance
                    "appearance-none m-0 p-0 border-0 bg-transparent font-inherit text-inherit",
                    // Layout
                    "w-full flex items-center gap-3 px-3 py-[10px] text-left",
                    // Visual
                    "rounded-[var(--radius-sm)] cursor-pointer transition-[background-color] duration-[100ms]",
                    "hover:bg-[var(--bg-hover)]",
                    isActive ? "bg-[var(--bg-canvas)]" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => pick(o)}
                >
                  {/* Name + meta */}
                  <span className="flex flex-col gap-[2px] min-w-0 flex-1">
                    <span className="text-[13px] font-medium text-[var(--fg-primary)] whitespace-nowrap overflow-hidden text-ellipsis">
                      {o.name}
                    </span>
                    <span className="text-[11.5px] leading-[1.3] text-[var(--fg-muted)]">
                      {o.type}
                      {o.type ? " · " : ""}
                      {o.region}
                    </span>
                  </span>

                  {/* Active indicator */}
                  {isActive && (
                    <span
                      className="font-mono text-[10px] font-medium uppercase tracking-[0.08em] text-[var(--fg-brand)] px-[7px] py-[3px] rounded-[var(--radius-sm)] shrink-0"
                      style={{
                        background:
                          "color-mix(in oklab, var(--brand-navy) 12%, transparent)",
                      }}
                    >
                      Current
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
